import { createElement, useEffect, useState } from "react";
import InfoField from "../../../../components/disposables/InfoField";
import { ConfigurationComponentType } from "../../../../navigation/ConfigurationNavigator";
import { ReactComponent as BluetoothSVG } from "../../../../assets/configuration/bluetooth.svg";
import { ReactComponent as WifiSVG } from "../../../../assets/configuration/wifi.svg";
import { ReactComponent as MartyDiagnosticsSVG } from "../../../../assets/configuration/marty-diagnostics.svg";
import { HINT_GREY, MAIN_BLUE } from "../../../../styles/colors";
import styles from "./styles.module.css";
import UpdateMartyName from "../../../../components/disposables/UpdateMartyName";
import ImageButton from "../../../../components/disposables/buttons/SVGImageButton";
import { ReactComponent as CalibrationImageSVG } from "../../../../assets/configuration/calibration.svg";
import wifiConfigSub, {
  FW_VERSION_RSSI_WIFI,
} from "../../../../utils/helpers/wifi-configuration-subtitle-gen";
import isVersionGreater from "../../../../utils/helpers/compare-version";
import ms from "../../../../state-observables/modal/ModalState";
import DisconnectConfirmationModal from "../../../../components/modals/DisconnectConfirmation";
import SimpleButton from "../../../../components/disposables/buttons/SimpleButton";
import openNewTab from "../../../../utils/open-new-tab";
import { ConnectedRaftItem } from "../../../../store/SelectedRaftContext";
import { RaftWifiConnState, RaftWifiConnStatus } from "@robotical/raftjs";
import Logger from "../../../../services/logger/Logger";
import { useTranslation } from "react-i18next";

interface ConfigurationScreenProps extends ConfigurationComponentType {
  connectedRaft: ConnectedRaftItem;
}

const SHOW_LOGS = true;
const TAG = "ConfigurationScreen";

function ConfigurationScreen({
  currentScreenSetter,
  connectedRaft
}: ConfigurationScreenProps) {
  const { t } = useTranslation();
  const [wifiCachedStatus, setWifiCachedStatus] = useState<RaftWifiConnStatus | undefined>(undefined);
  const [wifiIsLoading, setWifiIsLoading] = useState(false);

  const fetchAndSetWifiStatus = async () => {
    const wasSuccessful = await window.applicationManager.getWiFiConnStatusMarty(connectedRaft.id);
    if (!wasSuccessful) {
      Logger.error(SHOW_LOGS, TAG, "Failed to get wifi status");
      return;
    }
    const fetchedCachedStatus = window.applicationManager.getCachedWifiStatusMarty(connectedRaft.id);
    if (!fetchedCachedStatus) {
      Logger.error(SHOW_LOGS, TAG, "Wifi status is not cached");
      return;
    }
    setWifiCachedStatus(fetchedCachedStatus);
  }

  useEffect(() => {
    fetchAndSetWifiStatus();
  }, [connectedRaft.id]);


  const onPressWifi = async () => {
    const raft = window.applicationManager.connectedRafts[connectedRaft.id];
    if (!isVersionGreater(raft.getRaftVersion(), FW_VERSION_RSSI_WIFI)) return;
    setWifiIsLoading(true);

    let cachedStatus = window.applicationManager.getCachedWifiStatusMarty(connectedRaft.id);
    if (!cachedStatus) {
      await fetchAndSetWifiStatus();
      cachedStatus = window.applicationManager.getCachedWifiStatusMarty(connectedRaft.id);
      if (!cachedStatus) {
        Logger.error(SHOW_LOGS, TAG, "Failed to get wifi status");
        setWifiIsLoading(false);
        return;
      }
    }
    // check if wifi is currently connected or paused
    // if no wifi has been set up these will both be 0/false
    if (
      cachedStatus.connState === RaftWifiConnState.WIFI_CONN_CONNECTED &&
      cachedStatus.ipAddress
    ) {
      await window.applicationManager.wifiDisconnectMarty(connectedRaft.id);
      setWifiCachedStatus(undefined);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await fetchAndSetWifiStatus();
    } else {
      currentScreenSetter("wifi-configuration-step-1");
    }
    setWifiIsLoading(false);
  };

  console.log("wifiCachedStatus", wifiCachedStatus);

  return (
    <div className={styles["configuration-screen-container"]}>
      <div className={styles["configuration-screen-info-container"]}>
        <InfoField
          title={t("configuration_screen.bluetooth") || "Bluetooth"}
          subtitle={t("configuration_screen.connected") || "Connected"}
          buttonText={t("configuration_screen.disconnect") || "Disconnect"}
          buttonTextColour={"red"}
          IconSVG={BluetoothSVG}
          IconFill={MAIN_BLUE}
          onClick={() =>
            ms.setModal(
              createElement(DisconnectConfirmationModal),
              t("configuration_screen.disconnect_confirm") || "Are you sure you want to disconnect from your Marty?"
            )
          }
        />
        <InfoField
          title={t("configuration_screen.wifi") || "Wifi"}
          isLoading={wifiIsLoading}
          IconSVG={WifiSVG}
          IconFill={MAIN_BLUE}
          subtitle={wifiConfigSub(window.applicationManager.connectedRafts[connectedRaft.id], wifiCachedStatus)}
          buttonText={
            wifiCachedStatus?.connState ===
              RaftWifiConnState.WIFI_CONN_CONNECTED &&
              wifiCachedStatus?.ipAddress
              ? t("configuration_screen.disconnect") || "Disconnect"
              : t("configuration_screen.connect") || "Connect"
          }
          buttonTextColour={
            wifiCachedStatus?.connState ===
              RaftWifiConnState.WIFI_CONN_CONNECTED &&
              wifiCachedStatus?.ipAddress
              ? "red"
              : HINT_GREY
          }
          onClick={onPressWifi}
        />
        <InfoField
          title={t("configuration_screen.diagnostics", "Diagnostics") || "Diagnostics"}
          subtitle={t("configuration_screen.check_diagnostics") || "Check Marty Diagnostics"}
          buttonText={t("configuration_screen.open", "Open") || "Open"}
          buttonTextColour={HINT_GREY}
          IconSVG={MartyDiagnosticsSVG}
          IconFill={MAIN_BLUE}
          onClick={() => currentScreenSetter("diagnostics")}
        />
      </div>
      <div className={styles["configuration-screen-update-name-container"]}>
        <UpdateMartyName />
      </div>
      <div className={styles["configuration-screen-calibration-container"]}>
        <ImageButton
          SVGImage={CalibrationImageSVG}
          onClick={() => currentScreenSetter("calibration")}
          title={t("configuration_screen.calibrate") || "CALIBRATE"}
          subtitle={t("configuration_screen.keep_on_track", "Keep your Marty on track") || "Keep your Marty on track"}
        />
      </div>
      <div className={styles["configuration-screen-addons-container"]}>
        <ImageButton
          SVGImage={CalibrationImageSVG}
          onClick={() => currentScreenSetter("addons")}
          title={t("configuration_screen.addons") || "ADD-ONS"}
          subtitle={t("configuration_screen.manage_expansions") || "Manage Marty's expansions"}
        />
      </div>
      <div className={styles["configuration-screen-updates"]}>
        <SimpleButton
          onClick={() => openNewTab("https://updates.robotical.io/web-updater")}
          title={t("configuration_screen.update_marty") || "UPDATE YOUR MARTY"}
        />
        <h4 className={styles["marty-version-text"]}>{t("configuration_screen.current_version", { version: window.applicationManager.connectedRafts[connectedRaft.id].getRaftVersion() }) || `Current Version: ${window.applicationManager.connectedRafts[connectedRaft.id].getRaftVersion()}`}</h4>
      </div>
    </div>
  );
}

export default ConfigurationScreen;
